import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map, tap } from 'rxjs';
import { API_PATH, S3_URL_BASE } from 'src/environments/environment';

export interface HomeCarouselImage {
  id?: string;
  title: string;
  description: string;
  imageUrl: string;
  link: string;
  order: number;
  isActive: boolean;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface PaginatedResponse<T> {
  images: T[];
  total: number;
}

@Injectable({
  providedIn: 'root'
})
export class HomeCarouselImageService {
  private relativeUrl = 'home-carousel-image';

  constructor(protected httpClient: HttpClient) { }

  /**
   * Obter todas as imagens do carrossel
   * @param page Número da página (opcional, padrão: 0)
   * @param limit Itens por página (opcional, padrão: 6)
   */
  getAll(page: number = 0, limit: number = 6): Observable<HomeCarouselImage[]> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());
      
    return this.httpClient
      .get<PaginatedResponse<HomeCarouselImage>>(`${API_PATH}${this.relativeUrl}`, { params })
      .pipe(map(response => response.images));
  }

  /**
   * Obter o total de imagens e os dados paginados
   * @param page Número da página
   * @param limit Itens por página
   */
  getPaginated(page: number = 0, limit: number = 6): Observable<PaginatedResponse<HomeCarouselImage>> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());
    
    console.log(`Requisitando página ${page}, com limite ${limit}`);
    
    return this.httpClient.get<PaginatedResponse<HomeCarouselImage>>(
      `${API_PATH}${this.relativeUrl}`, 
      { params }
    ).pipe(
      tap(response => console.log('Resposta da API paginada:', response))
    );
  }

  /**
   * Obter apenas imagens ativas do carrossel
   */
  getActive(): Observable<HomeCarouselImage[]> {
    return this.httpClient.get<HomeCarouselImage[]>(`${API_PATH}${this.relativeUrl}/active`);
  }

  /**
   * Obter uma imagem específica por ID
   */
  getById(id: string): Observable<HomeCarouselImage> {
    return this.httpClient.get<HomeCarouselImage>(`${API_PATH}${this.relativeUrl}/${id}`);
  }

  /**
   * Criar uma nova imagem de carrossel (sem upload de arquivo)
   */
  create(image: HomeCarouselImage): Observable<HomeCarouselImage> {
    return this.httpClient.post<HomeCarouselImage>(`${API_PATH}${this.relativeUrl}`, image);
  }

  /**
   * Atualizar uma imagem existente (sem upload de arquivo)
   */
  update(id: string, image: HomeCarouselImage): Observable<any> {
    return this.httpClient.put(`${API_PATH}${this.relativeUrl}/${id}`, image);
  }

  /**
   * Excluir uma imagem do carrossel
   */
  delete(id: string): Observable<any> {
    return this.httpClient.delete(`${API_PATH}${this.relativeUrl}/${id}`);
  }

  /**
   * Obter uma imagem por sua ordem
   */
  getByOrder(order: number): Observable<HomeCarouselImage> {
    return this.httpClient.get<HomeCarouselImage>(`${API_PATH}${this.relativeUrl}/order/${order}`);
  }

  /**
   * Upload de uma nova imagem com arquivo
   * @param formData FormData contendo os dados da imagem e o arquivo
   */
  uploadImage(formData: FormData): Observable<HomeCarouselImage> {
    return this.httpClient.post<HomeCarouselImage>(`${API_PATH}${this.relativeUrl}`, formData);
  }

  /**
   * Atualizar uma imagem existente com arquivo
   * @param id ID da imagem a ser atualizada
   * @param formData FormData contendo os dados da imagem e o arquivo
   */
  updateWithFile(id: string, formData: FormData): Observable<any> {
    return this.httpClient.put(`${API_PATH}${this.relativeUrl}/${id}`, formData);
  }

  /**
   * Preparar FormData para envio ao servidor
   * @param imageData Dados básicos da imagem
   * @param file Arquivo de imagem (opcional)
   * @returns FormData pronto para envio
   */
  prepareFormData(imageData: Partial<HomeCarouselImage>, file?: File): FormData {
    const formData = new FormData();
    
    // Adicionar campos básicos
    if (imageData.title) formData.append('title', imageData.title);
    if (imageData.description) formData.append('description', imageData.description);
    if (imageData.link) formData.append('link', imageData.link);
    if (imageData.order !== undefined) formData.append('order', imageData.order.toString());
    if (imageData.isActive !== undefined) formData.append('isActive', imageData.isActive ? 'true' : 'false');
    
    // Se tiver URL de imagem existente e não tiver arquivo novo
    if (imageData.imageUrl && !file) {
      formData.append('imageUrl', imageData.imageUrl);
    }
    
    // Se tiver arquivo, adiciona ao FormData
    if (file) {
      formData.append('file', file);
    }
    
    return formData;
  }

  /**
   * Formata a URL da imagem para exibição, adicionando o prefixo do S3 se necessário
   * @param imageUrl URL da imagem
   * @returns URL formatada com o bucket da S3, se necessário
   */
  getFormattedImageUrl(imageUrl: string): string {
    if (!imageUrl) return '';
    
    if (imageUrl.startsWith('http')) {
      return imageUrl;
    } else {
      return `${S3_URL_BASE}${imageUrl}`;
    }
  }
} 